import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { queryClient } from 'src/core/react-query';
import type { OrganizationEntity } from 'src/core/types';

import axios from './axios';

export interface UseAddOrganizationEntitiesVariables {
  entities: Array<Omit<OrganizationEntity, 'id'>>;
}

export interface UseAddOrganizationEntitiesData {}

export function useAddOrganizationEntities(
  type: 'departments' | 'jobs' | 'locations',
) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    UseAddOrganizationEntitiesData,
    AxiosError<UseAddOrganizationEntitiesData>,
    UseAddOrganizationEntitiesVariables
  >(
    (variables) => {
      const { entities: data } = variables;

      return axios
        .post<UseAddOrganizationEntitiesData>(`/posht/ashena/${type}/`, data)
        .then((res) => res.data);
    },
    {
      onError: () => {
        enqueueSnackbar(t('errors.unknown'), { variant: 'error' });
      },
      onMutate: async (variables) => {
        await queryClient.cancelQueries(type);

        const previousData =
          queryClient.getQueryData<Array<OrganizationEntity>>(type);

        if (previousData) {
          const newData = [
            ...previousData,
            ...variables.entities.map((entity, index) => ({
              id: -index,
              is_active: entity.is_active,
              title: entity.title,
            })),
          ];

          queryClient.setQueryData<Array<OrganizationEntity>>(type, newData);
        }

        return { previousData };
      },
      onSettled: () => {
        queryClient.invalidateQueries(type);
      },
      onSuccess: () => {
        enqueueSnackbar(t('operation.successful'), { variant: 'success' });
      },
    },
  );
}
